import axios, { AxiosResponse } from 'axios'
import crypto from 'crypto-js'
import { AuthHeader, User } from 'services/types'
import { envConfig } from "../config";

type TokenResponse = {
    access_token: string,
    expires_in: number,
    id_token: string,
    refresh_token: string,
    scope: string,
    token_type: string
}

export enum AuthenticationStatus {
    Invalid = 'Invalid',
    Authenticated = 'Authenticated',
    Expired = 'Expired'
}

const config = envConfig();
const SCOPES: string[] = ['openid', 'profile', 'email', 'offline_access']
const BASE_URL: string = 'https://dotfoods.okta.com'
const CLIENT_ID: string | undefined = config.REACT_APP_OKTA_CLIENT_ID
const ISSUER: string = `${BASE_URL}/oauth2/default`
export const LOGIN_CALLBACK_URI: string = '/implicit/callback'
const APP_URL = `${window.location.origin}`
const LOGIN_REDIRECT_URI: string = `${APP_URL}${LOGIN_CALLBACK_URI}`
const POST_LOGOUT_REDIRECT_URI: string = `${APP_URL}`
const ROLE_PREFIX: string = 'DTI_LoadManager_'

const storagePrefix = 'dti-load-manager'
const storageKeys = {
    codeVerifier: `${storagePrefix}-auth-code-verifier`,
    nonce: `${storagePrefix}-auth-nonce`,
    state: `${storagePrefix}-auth-state`,
    accessToken: `${storagePrefix}-auth-access-token`,
    refreshToken: `${storagePrefix}-auth-refresh-token`,
    tokenExpires: `${storagePrefix}-auth-access-token-expires`,
    idToken: `${storagePrefix}-auth-id-token`,
    user: `${storagePrefix}-user`
}

const generateStateParam = () => {
    const randomCharset = 'abcdefghijklnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let random = '';
    for (let c = 0, cl = randomCharset.length; c < 64; ++c) {
        random += randomCharset[Math.floor(Math.random() * cl)];
    }
    return random;
}

const generateCodeChallenge = (codeVerifier: string) => {
    const hash = crypto.SHA256(codeVerifier)
    return hash.toString(crypto.enc.Base64).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

const clearLocalData = () => {
    localStorage.removeItem(storageKeys.accessToken)
    localStorage.removeItem(storageKeys.refreshToken)
    localStorage.removeItem(storageKeys.tokenExpires)
    localStorage.removeItem(storageKeys.idToken)
    localStorage.removeItem(storageKeys.state)
    localStorage.removeItem(storageKeys.nonce)
    localStorage.removeItem(storageKeys.codeVerifier)
    localStorage.removeItem(storageKeys.user)
}

const restartAuthProcess = (errorMessage?: string) => {
    errorMessage && console.error(errorMessage)
    clearLocalData()
    window.location.href = APP_URL
}

const getNewToken = async (authParams: URLSearchParams): Promise<string | undefined> => {
    try {
        const tokenResponse: AxiosResponse<TokenResponse> = await axios.post<TokenResponse>(`${BASE_URL}/oauth2/default/v1/token`, authParams)

        localStorage.setItem(storageKeys.accessToken, tokenResponse.data.access_token)
        localStorage.setItem(storageKeys.refreshToken, tokenResponse.data.refresh_token)
        localStorage.setItem(storageKeys.idToken, tokenResponse.data.id_token)
        localStorage.setItem(storageKeys.tokenExpires, (Date.now() + (tokenResponse.data.expires_in * 1000)).toString())
        return tokenResponse.data.access_token
    }
    catch(e: any) {
        const response = e.response
        //only need to restart the process if something goes wrong on okta's side
        if(response && response.status == 400) {
            restartAuthProcess("The refresh token is invalid. The login process must be restarted.")
        }
    }
}

const getAccessToken = async (): Promise<string | null> => {
    const authStatus = getStatus()

    if(authStatus == AuthenticationStatus.Authenticated) {
        return localStorage.getItem(storageKeys.accessToken)
    }
    else if(authStatus == AuthenticationStatus.Expired) {
        const refreshToken = localStorage.getItem(storageKeys.refreshToken)
        if(refreshToken) {
            await renewToken()
            return localStorage.getItem(storageKeys.accessToken)
        }
        else {
            await loginWithRedirect()
            return null
        }
    }
    else {
        await loginWithRedirect()
        return null
    }
} 

const getUser = (): User | null => {
    const user = localStorage.getItem(storageKeys.user)
    return user ? JSON.parse(user) : null
}

const loginWithRedirect = async () => {
    const codeVerifier = generateStateParam(),
    nonce = generateStateParam(),
    state = generateStateParam()

    const codeChallenge = generateCodeChallenge(codeVerifier)
    localStorage.setItem(storageKeys.codeVerifier, codeVerifier)
    localStorage.setItem(storageKeys.nonce, nonce)
    localStorage.setItem(storageKeys.state, state)

    const authParams = [
        `client_id=${CLIENT_ID}`,
        `redirect_uri=${encodeURIComponent(LOGIN_REDIRECT_URI)}`,
        `scope=${SCOPES.join('%20')}`,
        `response_type=code`,
        `code_challenge=${codeChallenge}`,
        `code_challenge_method=S256`,
        `nonce=${nonce}`,
        `state=${state}`
    ]

    window.location.href = `${BASE_URL}/oauth2/default/v1/authorize?${authParams.join('&')}`
}

const updateTokenFromCode = async (code: string) => {
    const codeVerifier = localStorage.getItem(storageKeys.codeVerifier) 

    if(codeVerifier == null) {
        restartAuthProcess("The code verifier could not be loaded or is missing. The login process must be restarted.")
    }

    let token: string | undefined = undefined
    try {
        let authParams = new URLSearchParams()
        authParams.append('client_id', CLIENT_ID ?? '')
        authParams.append('grant_type', 'authorization_code')
        authParams.append('redirect_uri', LOGIN_REDIRECT_URI)
        authParams.append('code', code ?? '')
        authParams.append('code_verifier', codeVerifier ?? '')
        
        token = await getNewToken(authParams)
    }
    catch (e) {
        restartAuthProcess("Could not get an oauth token. The login process must be restarted.")
    }

    try {
        if(token != null) {
            const userInfoResponse = await axios.get(`${BASE_URL}/oauth2/default/v1/userinfo`, { 
                headers: { Authorization: `Bearer ${token}` } 
            })
            localStorage.setItem(storageKeys.user, JSON.stringify(userInfoResponse.data))
        }
    }
    catch (e) {
        restartAuthProcess("Could not identify the user after getting a token. The login process must be restarted")
    }
}

const renewToken = async () => {
    const refreshToken = localStorage.getItem(storageKeys.refreshToken)

    if(refreshToken == null) {
        //no refresh token available. User must login to revive session
        await loginWithRedirect()
        return
    }

    try {
        let authParams = new URLSearchParams()
        authParams.append('client_id', CLIENT_ID ?? '')
        authParams.append('grant_type', 'refresh_token')
        authParams.append('refresh_token', refreshToken ?? '')
        
        await getNewToken(authParams)
    }
    catch (e) {
        restartAuthProcess('Could not get a new access token using the current refresh token.')
    }
    
}

const logout = async () => {
    const idToken = localStorage.getItem(storageKeys.idToken)
    if(idToken == null) {
        restartAuthProcess("Cannot logout because an ID token was not found. The session must be manually cleared.")
    }
    else {
        clearLocalData()
        window.location.href = `${BASE_URL}/oauth2/default/v1/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${POST_LOGOUT_REDIRECT_URI}`
    }
}

const getStatus = (): AuthenticationStatus => {
    const token = localStorage.getItem(storageKeys.accessToken)
    const expires = localStorage.getItem(storageKeys.tokenExpires)

    let status: AuthenticationStatus = AuthenticationStatus.Invalid
    if(token && expires) {
        const isExpired = Date.now() > Number(expires) 
        if(isExpired) {
            status = AuthenticationStatus.Expired
        }
        else {
            status = AuthenticationStatus.Authenticated
        }
    }
    return status
}

const getAuthHeader = async (contentType: string): Promise<AuthHeader> =>{ 
    let token = await getAccessToken()
    return {
        Authorization: `Bearer ${token}`,
        'Ocp-Apim-Subscription-Key': `${config.REACT_APP_APIM_KEY}`,
        'Content-Type': contentType,
    }
}

const getUserClaim = (claim: string): string | number | boolean | null => {
    let user = getUser()
    return user && user[claim]
}


export const authentication = {
    loginWithRedirect,
    updateTokenFromCode,
    renewToken,
    getStatus,
    logout,
    getAccessToken,
    getUser,
    getAuthHeader,
    getUserClaim
}